import React, { useState, useEffect, useMemo } from 'react';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import DataTable from '../../components/ui/DataTable';
import { PillTabs } from '../../components/ui/TabBar';
import {
  Bar,
  Line,
  Doughnut
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import {
  FaUsers,
  FaUserPlus,
  FaClock,
  FaGlobe,
  FaExternalLinkAlt,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaSyncAlt,
  FaMobileAlt,
  FaLaptop,
  FaTabletAlt,
  FaSearch,
  FaLink,
  FaBolt,
  FaFilter,
  FaFlag
} from 'react-icons/fa';
import axios from 'axios';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const [usersData, setUsersData] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [deviceStats, setDeviceStats] = useState([]);
  const [sourceStats, setSourceStats] = useState([]);
  const [browserStats, setBrowserStats] = useState([]);
  const [locationStats, setLocationStats] = useState([]);
  const [eventStats, setEventStats] = useState([]);
  const [conversionStats, setConversionStats] = useState([]);
  const [funnelStats, setFunnelStats] = useState([]);
  const [retentionStats, setRetentionStats] = useState([]);
  const [realtimeStats, setRealtimeStats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

      const endpoints = [
        'users', 'daily', 'pages', 'country', 'device',
        'source', 'browser', 'location', 'events',
        'conversion', 'funnel', 'retention', 'realtime'
      ];

      const responses = await Promise.all(
        endpoints.map(ep => axios.get(`${baseUrl}/api/analytics/${ep}`, config).catch(err => {
          console.warn(`Failed to fetch ${ep}:`, err);
          return { data: [] };
        }))
      );

      setUsersData(responses[0].data);
      setDailyTrend(responses[1].data.trend || responses[1].data);
      setTopPages(responses[2].data.pages || responses[2].data);
      setCountryStats(responses[3].data.countries || responses[3].data);
      setDeviceStats(responses[4].data.devices || responses[4].data);
      setSourceStats(responses[5].data.sources || responses[5].data);
      setBrowserStats(responses[6].data.browsers || responses[6].data);
      setLocationStats(responses[7].data.locations || responses[7].data);
      setEventStats(responses[8].data.events || responses[8].data);
      setConversionStats(responses[9].data.conversions || responses[9].data);
      setFunnelStats(responses[10].data.funnel || responses[10].data);
      setRetentionStats(responses[11].data.retention || responses[11].data);
      setRealtimeStats(responses[12].data);

      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error('Analytics Fetch Error:', err);
      setError('Failed to fetch analytics data. Please check your connection or credentials.');
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Chart Data Preparation
  const dailyChartData = useMemo(() => {
    if (!dailyTrend || dailyTrend.length === 0) return null;
    return {
      labels: dailyTrend.map(d => {
        const dateString = String(d.date);
        const date = dateString.length === 8
          ? `${dateString.substring(0, 4)}-${dateString.substring(4, 6)}-${dateString.substring(6, 8)}`
          : dateString;
        return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'Active Users',
          data: dailyTrend.map(d => d.users),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 6,
        }
      ]
    };
  }, [dailyTrend]);

  const countryChartData = useMemo(() => {
    if (!countryStats || countryStats.length === 0) return null;
    return {
      labels: countryStats.map(c => c.country),
      datasets: [
        {
          label: 'Users by Country',
          data: countryStats.map(c => c.users),
          backgroundColor: [
            '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
            '#06b6d4', '#f43f5e', '#ec4899', '#d946ef', '#a855f7'
          ],
          borderWidth: 0,
        }
      ]
    };
  }, [countryStats]);

  const deviceChartData = useMemo(() => {
    if (!deviceStats || deviceStats.length === 0) return null;
    return {
      labels: deviceStats.map(d => d.category),
      datasets: [
        {
          data: deviceStats.map(d => d.users),
          backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
          hoverOffset: 4
        }
      ]
    };
  }, [deviceStats]);

  const sourceChartData = useMemo(() => {
    if (!sourceStats || sourceStats.length === 0) return null;
    return {
      labels: sourceStats.map(s => s.source),
      datasets: [
        {
          label: 'Users by Source',
          data: sourceStats.map(s => s.users),
          backgroundColor: 'rgba(139, 92, 246, 0.8)',
          borderRadius: 8,
        }
      ]
    };
  }, [sourceStats]);

  const browserChartData = useMemo(() => {
    if (!browserStats || browserStats.length === 0) return null;
    return {
      labels: browserStats.map(b => b.browser),
      datasets: [
        {
          label: 'Users by Browser',
          data: browserStats.map(b => b.users),
          backgroundColor: 'rgba(6, 182, 212, 0.8)',
          borderRadius: 8,
        }
      ]
    };
  }, [browserStats]);

  if (loading) {
    return <SkeletonLoader variant="page" />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center bg-red-50 dark:bg-red-900/20 p-8 rounded-2xl max-w-md">
          <div className="bg-red-100 dark:bg-red-900/40 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExternalLinkAlt className="text-red-600 dark:text-red-400 text-2xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Oops! Something went wrong</h2>
          <p className="text-red-600 dark:text-red-400 font-medium mb-6">{error}</p>
          <button
            onClick={fetchData}
            className="px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-semibold shadow-lg shadow-primary-500/30"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const analyticsTabs = [
    { id: 'Overview', label: 'Overview' },
    { id: 'Tech Specs', label: 'Tech Specs' },
    { id: 'Acquisition', label: 'Acquisition' },
    { id: 'User Behavior', label: 'User Behavior' },
  ];

  return (
    <PageLayout>
      <PageHeader
        title="Analytics"
        description={`GA4 insights & real-time website tracking${usersData?.isMock ? ' • Demo Data' : ''}`}
        icon={FaChartLine}
        actions={
          <div className="flex items-center gap-3">
            {realtimeStats && (
              <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-lg border border-emerald-100 dark:border-emerald-900/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  {realtimeStats.activeUsers || 0} Live Users
                </span>
              </div>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all shadow-sm disabled:opacity-50"
            >
              <FaSyncAlt className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      <PillTabs tabs={analyticsTabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'Overview' && (
        <div className="space-y-6 animate-in fade-in duration-500">
          <StatGrid>
            <StatCard label="Active Users" value={usersData?.activeUsers || 0} icon={FaUsers} iconBg="bg-blue-100 dark:bg-blue-900/30" iconColor="text-blue-600 dark:text-blue-400" />
            <StatCard label="New Users" value={usersData?.newUsers || 0} icon={FaUserPlus} iconBg="bg-emerald-100 dark:bg-emerald-900/30" iconColor="text-emerald-600 dark:text-emerald-400" valueColor="text-emerald-600" />
            <StatCard label="Sessions" value={usersData?.sessions || 0} icon={FaGlobe} iconBg="bg-purple-100 dark:bg-purple-900/30" iconColor="text-purple-600 dark:text-purple-400" valueColor="text-purple-600" />
            <StatCard label="Bounce Rate" value={`${usersData?.bounceRate || 0}%`} icon={FaClock} iconBg="bg-amber-100 dark:bg-amber-900/30" iconColor="text-amber-600 dark:text-amber-400" valueColor="text-amber-600" />
          </StatGrid>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider">User Activity (Last 30 Days)</h3>
              </div>
              <div style={{ height: '320px' }}>
                {dailyChartData && (
                  <Line
                    data={dailyChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
                      scales: {
                        x: { grid: { display: false }, ticks: { font: { family: 'inherit', size: 11 } } },
                        y: { beginAtZero: true, grid: { color: 'rgba(156, 163, 175, 0.05)' }, border: { display: false }, ticks: { font: { family: 'inherit', size: 11 } } }
                      }
                    }}
                  />
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-6">Users by Country</h3>
              <div className="flex items-center justify-center mb-6" style={{ height: '220px' }}>
                {countryChartData && (
                  <Doughnut
                    data={countryChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '70%',
                      plugins: { legend: { display: false } }
                    }}
                  />
                )}
              </div>
              <div className="space-y-3">
                {countryStats.slice(0, 4).map((c, i) => (
                  <div key={i} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full`} style={{ backgroundColor: countryChartData?.datasets[0]?.backgroundColor[i] }}></div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{c.country}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{c.users}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Top Performing Pages</h3>
            <DataTable>
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                    <th className="px-4 py-4 text-left text-sm font-semibold">Page Path</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Page Views</th>
                    <th className="px-4 py-4 text-right text-sm font-semibold">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {topPages.map((page, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{page.path}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {page.views.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg ${i % 2 === 0 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30'}`}>
                          {i % 2 === 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.floor(Math.random() * 10) + 2}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>
          </div>
        </div>
      )}

      {activeTab === 'Tech Specs' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {/* Device Proportions */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-8 flex items-center gap-2">
              <FaLaptop className="text-blue-500" /> Device Categories
            </h3>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-48 h-48">
                {deviceChartData && <Doughnut data={deviceChartData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />}
              </div>
              <div className="flex-1 space-y-4 w-full">
                {deviceStats.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      {d.category === 'mobile' ? <FaMobileAlt className="text-emerald-500" /> : d.category === 'tablet' ? <FaTabletAlt className="text-amber-500" /> : <FaLaptop className="text-blue-500" />}
                      <span className="text-sm font-bold capitalize text-gray-700 dark:text-gray-300">{d.category}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{d.users} users</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Browser Usage */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-6">Browsers</h3>
            <div style={{ height: '300px' }}>
              {browserChartData && (
                <Bar
                  data={browserChartData}
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false }, beginAtZero: true },
                      y: { grid: { display: false } }
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Acquisition' && (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-6 flex items-center gap-2">
              <FaSearch className="text-purple-500" /> Traffic Sources
            </h3>
            <div style={{ height: '400px' }}>
              {sourceChartData && (
                <Bar
                  data={sourceChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false } },
                      y: { grid: { color: 'rgba(0,0,0,0.05)' } }
                    }
                  }}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sourceStats.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center text-purple-600">
                    <FaLink className="w-4 h-4" />
                  </div>
                  <span className="font-bold text-gray-800 dark:text-gray-200">{s.source}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{s.users}</p>
                  <p className="text-xs text-gray-500 uppercase">Users</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'User Behavior' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {/* Top Events */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-dark-800">
              <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <FaBolt className="text-amber-500" /> Most Frequent Events
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {eventStats.map((e, i) => (
                <div key={i} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-dark-800 rounded-xl transition-colors">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded-md">{e.name}</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{e.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Key Conversions */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-dark-800">
              <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <FaFlag className="text-emerald-500" /> Key Conversions
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {conversionStats.length > 0 ? conversionStats.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-dark-800 rounded-xl transition-colors">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{c.name}</span>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-2 bg-gray-100 dark:bg-dark-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${(c.count / Math.max(...conversionStats.map(x => x.count))) * 100}%` }}></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{c.count}</span>
                  </div>
                </div>
              )) : (
                <div className="p-8 text-center text-gray-500">No conversion data recorded yet.</div>
              )}
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="lg:col-span-2 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-8 flex items-center gap-2">
              <FaFilter className="text-primary-500" /> Conversion Funnel Analysis
            </h3>
            <div className="flex flex-col md:flex-row items-end justify-between gap-4 h-64">
              {funnelStats.map((step, i) => {
                const prevCount = i > 0 ? funnelStats[i - 1].count : step.count;
                const dropRate = i > 0 ? (100 - (step.count / prevCount) * 100).toFixed(1) : 0;

                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2 group relative">
                    <div
                      className="w-full bg-primary-500/10 border-t-4 border-primary-500 rounded-t-lg transition-all hover:bg-primary-500/20"
                      style={{ height: `${(step.count / funnelStats[0].count) * 100}%` }}
                    >
                      {i > 0 && (
                        <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-red-100 text-red-700 text-[10px] font-bold px-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          -{dropRate}% drop
                        </div>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">{step.count}</p>
                      <p className="text-[10px] font-bold text-gray-500 uppercase whitespace-nowrap">{step.step}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default Analytics;
